import {
  ChangeDetectorRef,
  Component,
  DoCheck,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  inject,
} from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { TabsModule } from 'primeng/tabs';
import { TooltipModule } from 'primeng/tooltip';
import { CadViewerComponent } from '../../../cad-viewer/cad-viewer.component';
import { CadPropertiesComponent } from '../../../shared/cad-properties/cad-properties.component';
import { CadObject } from '../../../../models/cad-object.model';
import { MouldingStepParameterValue } from '../../../../models/moulding-item.model';
import { CadObjectTreeComponent } from '../../../shared/cad-object-tree/cad-object-tree.component';
import { parseApiError } from '../../../quotes-api-error';
import { CadFileService } from '../../../services/cad-file.service';
import { MouldingGraphService } from '../../services/moulding-graph.service';
import { MouldingProcessingService } from '../../services/moulding-processing.service';
import {
  getNodeDefinition,
  NodeRunStatus,
  ProcessingNodeKind,
} from '../moulding-node-registry';

type GeometryProcessingNodeKind = Exclude<ProcessingNodeKind, 'dfm_check'>;

const HIGHLIGHT_OBJECT_NAMES: Partial<
  Record<GeometryProcessingNodeKind, string>
> = {
  add_structure: 'Backing Structure Attachments',
  drill_lt_holes: 'LT Hole Scribe Lines',
};

@Component({
  selector: 'app-processing-step-node-config-dialog',
  standalone: true,
  imports: [
    DialogModule,
    ButtonModule,
    TabsModule,
    TooltipModule,
    CadViewerComponent,
    CadPropertiesComponent,
    CadObjectTreeComponent,
  ],
  templateUrl: './processing-step-node-config-dialog.component.html',
})
export class ProcessingStepNodeConfigDialogComponent
  implements OnChanges, DoCheck
{
  private readonly graph = inject(MouldingGraphService);
  readonly processing = inject(MouldingProcessingService);
  private readonly cadFileService = inject(CadFileService);
  private readonly cdr = inject(ChangeDetectorRef);

  @Input({ required: true }) nodeId!: string;
  @Input({ required: true }) nodeKind!: GeometryProcessingNodeKind;
  @Input() visible = false;
  @Output() visibleChange = new EventEmitter<boolean>();

  // Starts true so the dialog's very first paint (as it fades in) already
  // shows the loading state; see the CAD Input / Pull Direction dialogs.
  loading = true;
  startErrorMessage: string | null = null;
  cadObjects: CadObject[] = [];
  cadObjectRoots: CadObject[] = [];
  hoveredCadObjectId: number | null = null;
  visibleCadObjectIds: Set<number> | null = null;

  private sourceCadObjectId: number | null = null;

  starting = false;
  showingAllParameters = false;

  get editableParameters(): Array<[string, MouldingStepParameterValue]> {
    return Object.entries(
      this.processing.getEffectiveStepParameters(this.nodeId, this.nodeKind),
    );
  }

  get primaryParameters(): Array<[string, MouldingStepParameterValue]> {
    return this.editableParameters.filter(
      ([name]) =>
        this.processing.getStepParameterPriority(this.nodeKind, name) ===
        'high',
    );
  }

  get secondaryParameters(): Array<[string, MouldingStepParameterValue]> {
    return this.editableParameters.filter(
      ([name]) =>
        this.processing.getStepParameterPriority(this.nodeKind, name) !==
        'high',
    );
  }

  // Tracked so ngDoCheck can detect a busy -> settled transition (driven by
  // WebSocket pushes into the node's runtime data) and reload the viewer to
  // show the freshly split result, without polling or a bespoke subject.
  private lastKnownStatus: NodeRunStatus = 'idle';

  get status(): NodeRunStatus {
    return this.getProcessingNodeData()?.status ?? 'idle';
  }

  get displayCadObject(): CadObject | null {
    const result = this.getProcessingNodeData()?.result;
    const cadObjectId =
      this.status === 'complete' && result
        ? result.artifact.primary_cad_object_id
        : this.sourceCadObjectId;
    return (
      this.cadObjects.find((cadObject) => cadObject.id === cadObjectId) ?? null
    );
  }

  get primaryCadObjectId(): number | null {
    const result = this.getProcessingNodeData()?.result;
    return this.status === 'complete' && result
      ? result.artifact.primary_cad_object_id
      : this.sourceCadObjectId;
  }

  get highlightCadObject(): CadObject | null {
    if (this.status !== 'complete') {
      return null;
    }
    const objectName = HIGHLIGHT_OBJECT_NAMES[this.nodeKind];
    return (
      this.cadObjects.find((cadObject) => cadObject.name === objectName) ?? null
    );
  }

  get runErrorMessage(): string | null {
    return this.getProcessingNodeData()?.errorMessage ?? null;
  }

  get dxfFileUrl(): string | null {
    return (
      this.getProcessingNodeData()?.result?.generated_files.find(
        (file) => file.kind === 'dxf',
      )?.url ?? null
    );
  }

  get resultCadFileId(): number | null {
    if (this.status !== 'complete') {
      return null;
    }
    return this.getProcessingNodeData()?.result?.artifact.cad_file_id ?? null;
  }

  get isBusy(): boolean {
    return this.status === 'pending' || this.status === 'processing';
  }

  get canStart(): boolean {
    return (
      !this.loading &&
      !this.isBusy &&
      !this.starting &&
      !this.processing.downstreamExecutionActive()
    );
  }

  get startButtonLabel(): string {
    const verb = this.getActionVerb();
    return this.status === 'complete' || this.status === 'error'
      ? `Restart ${verb}`
      : `Start ${verb}`;
  }

  get headerText(): string {
    return this.getNodeTitle();
  }

  get statusText(): string {
    const data = this.getProcessingNodeData();
    return data ? getNodeDefinition(this.nodeKind).describe(data) : '';
  }

  get parameterDescriptions(): Record<string, string> {
    return this.processing.getStepParameterDescriptions(this.nodeKind);
  }

  get stepDescription(): string {
    return this.processing.getStepDescription(this.nodeKind);
  }

  isBooleanParameterValue(value: MouldingStepParameterValue): boolean {
    return typeof value === 'boolean';
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['visible'] && this.visible) {
      this.showingAllParameters = false;
      void this.loadConnectedCad();
    }
  }

  ngDoCheck(): void {
    const currentStatus = this.status;
    if (currentStatus === this.lastKnownStatus) {
      return;
    }
    const wasBusy =
      this.lastKnownStatus === 'pending' ||
      this.lastKnownStatus === 'processing';
    this.lastKnownStatus = currentStatus;
    if (
      wasBusy &&
      (currentStatus === 'complete' || currentStatus === 'error')
    ) {
      void this.loadConnectedCad();
    }
  }

  onVisibleChange(visible: boolean): void {
    this.visible = visible;
    if (!visible) {
      this.hoveredCadObjectId = null;
    }
    this.visibleChange.emit(visible);
  }

  async onStart(): Promise<void> {
    if (!this.canStart) {
      return;
    }

    this.starting = true;
    this.startErrorMessage = null;
    try {
      await this.processing.startProcessingNode(this.nodeId, this.nodeKind);
      this.lastKnownStatus = this.status;
    } catch (error) {
      this.startErrorMessage = parseApiError(
        error,
        'Failed to start the processing job.',
      );
    } finally {
      this.starting = false;
    }
  }

  onClose(): void {
    this.onVisibleChange(false);
  }

  onParameterInputChange(
    key: string,
    currentValue: MouldingStepParameterValue,
    event: Event,
  ): void {
    const target = event.target as HTMLInputElement;
    const value =
      typeof currentValue === 'boolean'
        ? target.checked
        : Number.parseFloat(target.value);
    if (typeof value === 'number' && Number.isNaN(value)) {
      return;
    }

    const data = this.getProcessingNodeData();
    if (!data) {
      return;
    }
    this.graph.updateNodeData(this.nodeId, this.nodeKind, {
      parameters: {
        ...data.parameters,
        [key]: value,
      },
    });
  }

  toggleAllParameters(): void {
    this.showingAllParameters = !this.showingAllParameters;
  }

  onResetParameters(): void {
    this.graph.updateNodeData(this.nodeId, this.nodeKind, { parameters: {} });
  }

  formatParameterLabel(key: string): string {
    return key
      .split('_')
      .map((part) =>
        part === 'cte' ? 'CTE' : part.charAt(0).toUpperCase() + part.slice(1),
      )
      .join(' ');
  }

  formatParameterUnit(unit: string | null): string {
    return unit === 'deg' ? '°' : (unit ?? '');
  }

  private async loadConnectedCad(): Promise<void> {
    this.loading = true;
    this.cadObjects = [];
    this.cadObjectRoots = [];
    this.hoveredCadObjectId = null;
    this.lastKnownStatus = this.status;

    const result = this.getProcessingNodeData()?.result ?? null;
    if (this.status !== 'complete' || result === null) {
      this.sourceCadObjectId = null;
      this.loading = false;
      this.cdr.detectChanges();
      return;
    }

    try {
      this.sourceCadObjectId = result.artifact.primary_cad_object_id;
      const cadFile = await firstValueFrom(
        this.cadFileService.getCadFile(result.artifact.cad_file_id),
      );
      this.cadObjectRoots = cadFile.cad_objects ?? [];
      this.cadObjects = await firstValueFrom(
        this.cadFileService.loadCadObjectGeometry(this.cadObjectRoots),
      );
    } catch {
      // Leave the viewer empty when the CAD cannot be loaded.
    } finally {
      this.loading = false;
      this.cdr.detectChanges();
    }
  }

  private getProcessingNodeData() {
    return this.graph.getNodeData(this.nodeId, this.nodeKind);
  }

  private getNodeTitle(): string {
    switch (this.nodeKind) {
      case 'split_part':
        return 'Split Part';
      case 'fill_holes_and_cutouts':
        return 'Fill Holes and Cutouts';
      case 'add_runoff':
        return 'Add Runoff';
      case 'scale_cad':
        return 'Scale CAD';
      case 'create_mold_box':
        return 'Create Mold Box';
      case 'create_pattern':
        return 'Create Pattern';
      case 'drill_lt_holes':
        return 'Add LT Holes';
      case 'add_structure':
        return 'Add Backing Structure';
    }
  }

  private getActionVerb(): string {
    switch (this.nodeKind) {
      case 'split_part':
        return 'Split';
      case 'fill_holes_and_cutouts':
        return 'Fill';
      case 'add_runoff':
        return 'Runoff';
      case 'scale_cad':
        return 'Scaling';
      case 'create_mold_box':
        return 'Mold Box';
      case 'create_pattern':
        return 'Pattern';
      case 'drill_lt_holes':
        return 'LT Holes';
      case 'add_structure':
        return 'Backing Structure';
    }
  }
}
